import { create } from 'zustand'

interface Toast {
  id: string
  type: 'info' | 'success' | 'error'
  message: string
}

type Theme = 'dark' | 'light'

interface UiState {
  sidebarOpen: boolean
  toasts: Toast[]
  mediaViewerUrl: string | null
  replyTo: { chatId: string; messageId: string; text: string } | null
  editMessage: { chatId: string; messageId: string; text: string } | null
  highlightedMessageId: string | null
  theme: Theme
  wallpaper: string | null
  chatWallpapers: Record<string, string>
  fontSize: number
  soundEnabled: boolean
  emojiStatus: string
  sendOnEnter: boolean

  setHighlightedMessage: (id: string | null) => void
  setEmojiStatus: (emoji: string) => void
  setChatWallpaper: (chatId: string, wallpaper: string) => void
  clearChatWallpaper: (chatId: string) => void

  globalSearchOpen: boolean
  setGlobalSearchOpen: (v: boolean) => void

  toggleSidebar: () => void
  setSidebarOpen: (v: boolean) => void

  addToast: (type: Toast['type'], message: string) => void
  removeToast: (id: string) => void

  openMedia: (url: string) => void
  closeMedia: () => void

  setReplyTo: (v: UiState['replyTo']) => void
  clearReplyTo: () => void

  setEditMessage: (v: UiState['editMessage']) => void
  clearEditMessage: () => void

  toggleTheme: () => void
  setWallpaper: (url: string | null) => void
  setFontSize: (n: number) => void
  toggleSound: () => void
  setSendOnEnter: (v: boolean) => void
}

let toastSeq = 0

const initTheme = (): Theme => {
  const saved = localStorage.getItem('theme') as Theme | null
  const theme = saved === 'light' ? 'light' : 'dark'
  document.documentElement.setAttribute('data-theme', theme)
  return theme
}

const initFontSize = (): number => {
  const saved = localStorage.getItem('font-size')
  const n = saved ? Number(saved) : 14
  document.documentElement.style.setProperty('--font-size-base', `${n}px`)
  return n
}

export const useUiStore = create<UiState>()((set) => ({
  sidebarOpen: typeof window !== 'undefined' ? window.innerWidth >= 768 : true,
  toasts: [],
  mediaViewerUrl: null,
  replyTo: null,
  editMessage: null,
  highlightedMessageId: null,
  globalSearchOpen: false,
  theme: typeof window !== 'undefined' ? initTheme() : 'dark',
  wallpaper: typeof window !== 'undefined' ? localStorage.getItem('wallpaper') : null,
  fontSize: typeof window !== 'undefined' ? initFontSize() : 14,
  soundEnabled: typeof window !== 'undefined' ? localStorage.getItem('sound') !== 'off' : true,
  emojiStatus: typeof window !== 'undefined' ? (localStorage.getItem('emoji-status') ?? '') : '',
  sendOnEnter: typeof window !== 'undefined' ? localStorage.getItem('send-on-enter') !== 'false' : true,
  chatWallpapers: typeof window !== 'undefined' ? (() => { try { return JSON.parse(localStorage.getItem('chat-wallpapers') ?? '{}') } catch { return {} } })() : {},

  setHighlightedMessage: (id) => set({ highlightedMessageId: id }),
  setEmojiStatus: (emoji) => set(() => { localStorage.setItem('emoji-status', emoji); return { emojiStatus: emoji } }),
  setChatWallpaper: (chatId, wallpaper) => set((s) => {
    const next = { ...s.chatWallpapers, [chatId]: wallpaper }
    localStorage.setItem('chat-wallpapers', JSON.stringify(next))
    return { chatWallpapers: next }
  }),
  clearChatWallpaper: (chatId) => set((s) => {
    const next = { ...s.chatWallpapers }
    delete next[chatId]
    localStorage.setItem('chat-wallpapers', JSON.stringify(next))
    return { chatWallpapers: next }
  }),

  setGlobalSearchOpen: (v) => set({ globalSearchOpen: v }),

  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
  setSidebarOpen: (v) => set({ sidebarOpen: v }),

  addToast: (type, message) => {
    const id = String(++toastSeq)
    set((s) => ({ toasts: [...s.toasts, { id, type, message }] }))
    setTimeout(() => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })), 4000)
  },

  removeToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),

  openMedia: (url) => set({ mediaViewerUrl: url }),
  closeMedia: () => set({ mediaViewerUrl: null }),

  setReplyTo: (v) => set({ replyTo: v }),
  clearReplyTo: () => set({ replyTo: null }),

  setEditMessage: (v) => set({ editMessage: v }),
  clearEditMessage: () => set({ editMessage: null }),

  toggleTheme: () =>
    set((s) => {
      const next: Theme = s.theme === 'dark' ? 'light' : 'dark'
      localStorage.setItem('theme', next)
      document.documentElement.setAttribute('data-theme', next)
      return { theme: next }
    }),

  setWallpaper: (url) =>
    set(() => {
      if (url) localStorage.setItem('wallpaper', url)
      else localStorage.removeItem('wallpaper')
      return { wallpaper: url }
    }),

  setFontSize: (n) =>
    set(() => {
      document.documentElement.style.setProperty('--font-size-base', `${n}px`)
      localStorage.setItem('font-size', String(n))
      return { fontSize: n }
    }),

  toggleSound: () =>
    set((s) => {
      const next = !s.soundEnabled
      if (next) localStorage.removeItem('sound')
      else localStorage.setItem('sound', 'off')
      return { soundEnabled: next }
    }),

  setSendOnEnter: (v) =>
    set(() => {
      if (v) localStorage.removeItem('send-on-enter')
      else localStorage.setItem('send-on-enter', 'false')
      return { sendOnEnter: v }
    }),
}))
